import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { IconAlert } from '@/components/Icons';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Wordmark } from '@/components/Wordmark';
import { LoginMigrationBanner } from '@/components/MigrationBanner';

export function LoginPage() {
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(identifier.trim(), password);
    } catch (caught) {
      // A mensagem do servidor é a que importa aqui ("Usuário ou senha
      // incorretos"), então ela passa direto em vez de virar texto genérico.
      setError(
        caught instanceof ApiError
          ? caught.fieldMessage ?? caught.message
          : 'Não foi possível entrar. Tente novamente.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <LoginMigrationBanner />
      <div style={{ minHeight: '100vh', display: 'flex' }}>
        {/* A vitrine do kit: violeta-profundo nos dois temas, porque é a marca
            falando, não a interface. Some no celular, onde o formulário é a
            única coisa que importa. */}
        <aside
          className="login-aside"
          style={{
            width: '48%',
            maxWidth: 640,
            background: 'var(--showcase)',
            color: 'var(--on-showcase)',
            padding: '56px 60px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Wordmark size="lg" tone="showcase" />

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 440 }}>
            <h1
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(40px, 3.6vw, 54px)',
                fontWeight: 400,
                lineHeight: 1.04,
                letterSpacing: '-0.015em',
                margin: '0 0 22px',
              }}
            >
              Cada venda,
              <br />
              <em style={{ color: 'var(--showcase-accent)' }}>com a prova.</em>
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--on-showcase-soft)', margin: 0 }}>
              Comprovantes, repasses e metas em um só painel — e um ranking que mostra,
              em tempo real, quem está vendendo mais.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              ['Comprovante', 'print ou PDF'],
              ['Ranking', 'por valor vendido'],
              ['Metas', 'meta x vendido'],
            ].map(([title, sub]) => (
              <div
                key={title}
                style={{
                  minWidth: 0,
                  flex: '1 1 120px',
                  padding: '12px 14px',
                  border: '1px solid var(--showcase-line)',
                  borderRadius: 'var(--radius-xl)',
                  background: 'var(--showcase-raised)',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--on-showcase-soft)' }}>{sub}</div>
              </div>
            ))}
          </div>
        </aside>

        {/* Formulário */}
        <main
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', position: 'relative' }}
        >
          <div style={{ position: 'absolute', top: 18, right: 18 }}>
            <ThemeToggle />
          </div>
          <div style={{ width: '100%', maxWidth: 380 }}>
            <div className="login-mark-mobile" style={{ display: 'none', marginBottom: 36 }}>
              <Wordmark size="lg" />
            </div>

            <h2 style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.15, margin: '0 0 8px', letterSpacing: '-0.035em' }}>
              Acesse sua conta
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-soft)', margin: '0 0 30px' }}>
              Use o seu usuário — ou o e-mail, se tiver um cadastrado.
            </p>

            <form onSubmit={onSubmit} className="login-form" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <label className="field">
                <span className="field-label">Usuário ou e-mail</span>
                <input
                  className="inp"
                  type="text"
                  name="username"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder="seu.usuario"
                />
              </label>

              <label className="field">
                <span className="field-label">Senha</span>
                <input
                  className="inp"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                />
              </label>

              {error ? (
                <div className="alert alert-error" role="alert">
                  <IconAlert />
                  <span>{error}</span>
                </div>
              ) : null}

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={busy || !identifier.trim() || !password}
                style={{ minHeight: 44, fontSize: 15, marginTop: 4 }}
              >
                {busy ? <span className="spinner" /> : null}
                {busy ? 'Entrando…' : 'Entrar'}
              </button>
            </form>

            <p
              style={{
                fontSize: 13,
                marginTop: 26,
                lineHeight: 1.6,
                padding: '12px 14px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-xl)',
                background: 'var(--sunken)',
                color: 'var(--ink-soft)',
              }}
            >
              <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>Sem acesso ou esqueceu a senha?</strong> Peça a um
              administrador — ele cria o seu usuário e define uma senha nova.
            </p>
          </div>
        </main>

        {/* !important nos dois: as regras disputam com o `display` inline. Na
            entrada os campos têm 44 px e letra de 15 px também no desktop. */}
        <style>{`
          .login-form .inp { min-height: 44px; font-size: 15px; padding: 10px 13px; }
          @media (pointer: coarse) {
            .login-form .inp { font-size: 16px; }
          }
          @media (max-width: 860px) {
            .login-aside { display: none !important; }
            .login-mark-mobile { display: block !important; }
          }
        `}</style>
      </div>
    </>
  );
}
